/**
 * FINAL PROJECT index (M2): the student's active cohorts, each with a
 * shortcut to its final project. Gated by `academy.final_project`. Managers
 * reach any cohort's detail via URL/link; this list is the student's
 * access point.
 */
import { createRoute } from "@tanstack/solid-router";
import { Link } from "@tanstack/solid-router";
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ChevronRight, Trophy } from "@fleet/icons";
import { withFeatureGate } from "@/features/flags/feature-gate";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { useCohort, useMyCohortEnrollments } from "@/features/academy/hooks/use-cohort-data";

const CohortLink: Component<{ cohortId: string }> = (props) => {
  const cohort = useCohort(() => props.cohortId);
  return (
    <Link
      to="/final-project/$cohortId"
      params={{ cohortId: props.cohortId }}
      class="flex items-center justify-between gap-2 rounded-2xl border border-border bg-surface p-4 hover:border-accent"
      data-testid="final-project-cohort-link"
    >
      <span class="flex items-center gap-2">
        <Trophy class="h-4 w-4 text-accent" />
        <span class="font-semibold">{cohort.data?.name ?? props.cohortId}</span>
      </span>
      <ChevronRight class="h-4 w-4 text-muted-foreground" />
    </Link>
  );
};

function FinalProjectIndexPage() {
  const { t } = useI18n();
  const enrollments = useMyCohortEnrollments();
  const activeCohorts = createMemo(() =>
    (enrollments.data ?? []).filter((e) => e.status === "active" || e.status === "completed"),
  );

  return (
    <PageContainer maxWidth="3xl">
      <PageHeader
        title={t(msg`Final project`)}
        description={t(msg`The capstone of each cohort you are enrolled in`)}
        icon={Trophy}
      />
      <div class="mt-4">
        <Show when={!enrollments.isLoading} fallback={<Skeleton class="h-24 w-full rounded-2xl" />}>
          <Show
            when={activeCohorts().length > 0}
            fallback={
              <p class="text-muted-foreground" data-testid="final-project-index-empty">
                {t(msg`You have no active cohorts yet.`)}
              </p>
            }
          >
            <div class="space-y-2">
              <For each={activeCohorts()}>{(e) => <CohortLink cohortId={e.cohort_id ?? ""} />}</For>
            </div>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
}

export const finalProjectIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/final-project",
  component: withFeatureGate("academy.final_project", FinalProjectIndexPage),
});
